import styled, { css } from 'styled-components';
import { Button } from '@lmn/design-system';
import { pillTrailingSpace } from '@/app/common/components/Pill/styled.ts';
import { showOnlyOnDesktopInSearchPage, whenDesktopLayoutIsNarrow } from '@/app/common/css/mixins.ts';

// ─── Shared primitives ────────────────────────────────────────────────────────

export const GrayButton = styled(Button)`
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  text-decoration: underline dotted;
  text-decoration-thickness: 13%;
  text-underline-position: from-font;
  min-height: 40px;
  padding: 0 ${({ theme }) => theme.kallisto.space.space100};
  margin: 0 calc(-1 * ${({ theme }) => theme.kallisto.space.space100});
  background: transparent !important;
  transition: none;

  &:not(:disabled):hover {
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary} !important;
  }

  &:disabled {
    color: ${({ theme }) => theme.kallisto.color.text.static.textDisabled};
    cursor: not-allowed;
  }
`;

// Busy while a search runs, but kept focusable with aria-disabled (MPH-261), so this mirrors the outlined
// Button's own :disabled look. && outweighs the Button's :hover:not(:disabled) and :active:not(:disabled).
export const ViewModeButton = styled(Button)`
  flex-shrink: 0;

  &&[aria-disabled='true'],
  &&[aria-disabled='true']:hover,
  &&[aria-disabled='true']:active {
    color: ${({ theme }) => theme.kallisto.color.text.static.textDisabled};
    border-color: ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
    background-color: ${({ theme }) => theme.kallisto.color.common.white[1000]};
    cursor: not-allowed;
  }
`;

// ─── Shell (outer wrapper) ────────────────────────────────────────────────────

export const HeaderShell = styled.div<{ isWaiting: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 24px;
  transition: height 0.2s ease-in-out;
  padding: 24px 0;
  background: white;

  b {
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  }

  ${whenDesktopLayoutIsNarrow(css`
    padding: 0;
    gap: 0;

    @media (min-width: 768px) {
      padding: 0 24px;
    }
  `)};

  ${({ isWaiting }) =>
    isWaiting &&
    css`
      @media (max-width: 767px) {
        body:has(.no-results) & {
          display: none;
        }
      }
    `};
`;

// ─── Heading row (title text + view-mode toggle) ──────────────────────────────

export const HeadingRow = styled.div<{ variant?: 'list' | 'map' }>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 16px;
  width: 100%;
  padding: 0 24px;
  align-items: center;
  ${showOnlyOnDesktopInSearchPage};

  ${({ variant }) => {
    if (variant === 'list')
      return css`
        z-index: 4;
        margin-top: 8px;
        padding: 0;
      `;

    if (variant === 'map') {
      return css``;
    }

    return css``;
  }}
`;

export const HeadingText = styled.div<{ isWaiting: boolean }>`
  font-style: normal;
  font-size: 20px;
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight['350']};
  position: relative;
  width: 100%;

  @media (min-width: 768px) {
    ${({ theme }) => theme.kallisto.mixins.font('Title1')};
  }
`;

// ─── Sort bar row (sort pill + CO₂ / ATOL badges) ────────────────────────────

export const SortBarBadges = styled.div`
  display: contents;

  ${whenDesktopLayoutIsNarrow(css`
    @media (max-width: 767px) {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;

      svg {
        width: 32px;
        height: 32px;
      }
    }
  `)};
`;

export const SortBarRow = styled.div<{ variant?: 'list' | 'map' }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
  padding: 0 24px;
  justify-content: space-between;
  align-items: center;

  ${pillTrailingSpace};

  ${({ variant }) =>
    variant === 'list' &&
    css`
      margin-top: 8px;
      margin-bottom: 24px;
      padding: 0;
    `}

  ${({ variant }) =>
    whenDesktopLayoutIsNarrow(css`
      @media (max-width: 767px) {
        gap: 0;
        margin: 0 0 16px;
        padding: 0 16px;

        & > :not(${SortBarBadges}) {
          display: none;
        }
      }

      ${
        variant !== 'list' &&
        css`
          @media (min-width: 768px) {
            margin: 8px 0 24px;
            padding: 0;
          }
        `
      }
    `)};
`;
